import Bus from '../../bus'

import {
  isEmpty
} from 'lodash'

import {
  logger
} from '../../utils'

import ApiManager from '../../api/request'
import {
  getClientProfile,
  login,
  registation as registrationRequest,
  restorePassword,
  registerWithForm,
  profileUpdate,
  updateUserLanguage,
  passwordUpdate,
  logout,
  loginWithForm,
  initPasswordResetWithForm,
  passwordReset,
  getClientPreferences,
  setClientPreferences,
  profileUpdateWithForm,
  changePasswordWithForm,
  passwordResetWithForm,
  updateMarketingInfo,
  updateLimitsSet,
  closeAccount
} from '../../api/auth'

import StorageManager from '../storage'
import AnalyticsManager from '../analytics'
import SettingsManager from '../settings'

export const AUTH_TOKEN_LOCAL_STORAGE_KEY = 'tk'

class AccountManagerError extends Error {
  constructor (message) {
    super(message)
    this.name = 'AccountManagerError'
  }
}

/*
  Type for submitting constructed forms to API form service
  @typedef {Object} FormOptions
  @property {int} id
  @property {Object} values
*/

class AccountManager {
  constructor () {
    this.account = {}
    this.authenticated = false
  }

  storeAccountData ({ clientData }) {
    // Storing account data
    this.account = {
      ...this.account,
      ...clientData
    }

    Bus.send({
      event: Bus.events.profile.accountUpdate,
      data: this.account
    })

    this.authenticated = true
  }

  getAccountCurrency () {
    if (this.account && this.account.currency) {
      return this.account.currency
    }

    return undefined
  }

  cleanAccountData () {
    this.account = {}
    this.authenticated = false
  }

  async isTokenExist () {
    const token = await StorageManager.getItem(AUTH_TOKEN_LOCAL_STORAGE_KEY)

    return !!token
  }

  setToken (token) {
    return StorageManager.setItem(
      AUTH_TOKEN_LOCAL_STORAGE_KEY,
      token
    )
  }

  rmToken () {
    return StorageManager.removeItem(
      AUTH_TOKEN_LOCAL_STORAGE_KEY
    )
  }

  getToken () {
    return StorageManager.getItem(
      AUTH_TOKEN_LOCAL_STORAGE_KEY
    )
  }

  /*
    Method for resolving authentication and setting client profile
    Used on init phase, when application starts
  */
  async resolveAuthentication () {
    const token = await this.getToken()

    if (!token) {
      Bus.send({
        event: Bus.events.auth.unresolved
      })

      throw new AccountManagerError('no token found')
    }

    // Resolving token by setting it in API service
    ApiManager.setAuthorizationToken(token)

    Bus.send({
      event: Bus.events.auth.resolving
    })

    return getClientProfile()
      .then(clientAndWallet => {
        Bus.send({
          event: Bus.events.auth.resolved,
          data: clientAndWallet
        })
        this.storeAccountData({
          clientData: clientAndWallet.client
        })
        return clientAndWallet
      })
      .catch(error => {
        this.rmToken()

        ApiManager.setAuthorizationToken(null)

        Bus.send({
          event: Bus.events.auth.unresolved,
          error
        })
      })
  }

  /*
    Basic registration implementation
    Used in autologin

    @param {Object} signin credentials (login & password)
  */
  login (credentials) {
    logger.debug('AuthService.login', credentials)

    return login(credentials)
      .then(data => {
        // Saving session token to local storage
        this.setToken(data.sessionToken)

        // Setting session token in API service
        ApiManager.setAuthorizationToken(data.sessionToken)

        return data
      })
      .then(() => {
        return getClientProfile()
      })
      .then(clientAndWallet => {
        Bus.send({
          event: Bus.events.auth.login,
          data: clientAndWallet
        })
        this.storeAccountData({
          clientData: clientAndWallet.client
        })
        return clientAndWallet
      })
      .catch(error => {
        Bus.send({
          event: Bus.events.auth.loginFailed,
          error
        })

        throw error
      })
  }

  /*
    Form service login implementation
    Used with form constructor functionality

    @param {FormData} formData
  */
  loginWithForm (formData) {
    logger.debug('AuthService.login with form', formData)

    const {
      id,
      ...values
    } = formData

    const meta = AnalyticsManager.getAnalytics()

    return loginWithForm({
      id,
      values,
      ...meta
    })
      .then(data => {
        // Saving session token to local storage
        this.setToken(data.sessionToken)

        // Setting session token in API service
        ApiManager.setAuthorizationToken(data.sessionToken)

        return data
      })
      .then(() => {
        return getClientProfile()
      })
      .then(clientAndWallet => {
        Bus.send({
          event: Bus.events.auth.login,
          data: clientAndWallet
        })
        this.storeAccountData({
          clientData: clientAndWallet.client
        })
        return clientAndWallet
      })
      .catch(error => {
        Bus.send({
          event: Bus.events.auth.loginFailed,
          error
        })

        throw error
      })
  }

  /*
    Basic registration implementation

    Used only in simple registation strategy
    Not using form implementation

    @param {FormData} form registration data
  */
  async registation (data) {
    logger.debug('AuthService.registationData', data)

    try {
      // Invoking API registration method
      const registationResponseData = await registrationRequest(data)

      Bus.send({
        event: Bus.events.auth.registation,
        data: registationResponseData
      })

      // Intercepting registration with login (autologin feature)
      // Invoke login and wait till flow finish
      await this.login(data)

      return registationResponseData
    } catch (error) {
      Bus.send({
        event: Bus.events.auth.registrationFailed,
        error
      })

      throw error
    }
  }

  /*
    Form service registration implementation

    Used with form constructor functionality

    @param {FormData} formData
  */
  formRegistation (formData) {
    logger.debug('AuthService.registationData', formData)

    const {
      id,
      ...values
    } = formData

    const meta = AnalyticsManager.getAnalytics()

    return registerWithForm({
      id,
      values,
      ...meta
    }).then(data => {
      Bus.send({
        // event: Bus.events.auth.formRegistationSuccess,
        event: Bus.events.auth.registation,
        data
      })
      return this.login(data).then(() => {
        return data
      })
    })
      .catch(error => {
        Bus.send({
          event: Bus.events.auth.registrationFailed,
          error
        })
        throw error
      })
  }

  /*
    Method for updating account profile

    @param {FormData} data - updated profile
  */
  async profileUpdate (data) {
    if (isEmpty(data)) {
      throw new AccountManagerError('No data for update profile')
    }

    logger.debug('AuthService.profileUpdate', data)

    try {
      const clientData = await profileUpdate(data)
      this.storeAccountData({
        clientData
      })
      return clientData
    } catch (error) {
      throw new AccountManagerError('Unable to update profile: ' + error.message)
    }
  }

  async updateUserLanguage (language) {
    // Instantly updating settings language to display it in UI
    SettingsManager.updateSettings({
      lang: language
    })

    // Invoking server update only for authenticated
    if (this.authenticated) {
      // If stored account language is different
      if (this.account.language !== language) {
        try {
          const clientData = await updateUserLanguage({
            language
          })

          logger.debug('[account]', 'account language updated', clientData)

          this.storeAccountData({
            clientData
          })
        } catch (error) {
          logger.debug('[account]', 'unable to update account language', error)
        }
      }
    }
  }

  async updateMarketingInfo ({ marketingOptIn, emailOptIn, smsOptIn, phoneOptIn, chatOptIn }) {
    // Instantly updating settings to display it in UI
    SettingsManager.updateSettings({
      marketingOptIn: marketingOptIn,
      emailOptIn: emailOptIn,
      smsOptIn: smsOptIn,
      phoneOptIn: phoneOptIn,
      chatOptIn: chatOptIn
    })

    try {
      const clientData = await updateMarketingInfo({
        marketingOptIn,
        emailOptIn,
        smsOptIn,
        phoneOptIn,
        chatOptIn
      })

      logger.debug('[account]', 'account marketing info updated', clientData)

      this.storeAccountData({
        clientData
      })
    } catch (error) {
      logger.debug('[account]', 'unable to update account marketing info', error)
    }
  }

  async updateLimitsSet ({ limitsSet }) {
    // Instantly updating settings to display it in UI
    SettingsManager.updateSettings({
      limitsSet: limitsSet
    })

    try {
      const clientData = await updateLimitsSet({
        limitsSet
      })

      logger.debug('[account]', 'account limitsSet updated', clientData)

      Bus.send({
        event: Bus.events.profile.accountUpdate,
        data: clientData
      })
    } catch (error) {
      logger.debug('[account]', 'unable to update account limitsSet', error)
    }
  }

  async closeAccount ({ selfClosed }) {
    // Instantly updating settings to display it in UI
    SettingsManager.updateSettings({
      selfClosed: selfClosed
    })

    try {
      const clientData = await closeAccount({
        selfClosed
      })
      logger.debug('[account]', 'account limitsSet updated', clientData)

      Bus.send({
        event: Bus.events.profile.accountUpdate,
        data: clientData
      })
    } catch (error) {
      logger.debug('[account]', 'unable to update account limitsSet', error)
    }
  }

  async profileUpdateWithForm (data) {
    const {
      id,
      ...values
    } = data

    const clientData = await profileUpdateWithForm({
      id,
      values
    })

    this.storeAccountData({
      clientData
    })

    return clientData
  }

  /*
    Fetching client preferences
    Updating preferences in application state
  */
  async loadClientPreferences () {
    try {
      const { preferences } = await getClientPreferences()

      Bus.send({
        event: Bus.events.profile.preferencesUpdate,
        data: preferences
      })

      return preferences
    } catch (error) {
      logger.warn(
        '[account]',
        'loading profile preferences failed',
        error.message
      )
    }
  }

  /*
    Updating client preferences
    Updating preferences in application state
  */
  async setClientPreferences (preferences) {
    try {
      await setClientPreferences(preferences)

      Bus.send({
        event: Bus.events.profile.preferencesUpdate,
        data: preferences
      })
    } catch (error) {
      throw new AccountManagerError(`Update profile preferences failed: ${error.message}`)
    }
  }

  passwordUpdate (data) {
    logger.debug('AuthService.passwordUpdate', data)
    return passwordUpdate(data)
  }

  changePasswordWithForm (data) {
    const {
      id,
      ...values
    } = data

    return changePasswordWithForm({ id, values })
  }

  logout () {
    return logout()
      .finally(() => {
        this.rmToken()
        this.cleanAccountData()
      })
  }

  // TODO: Remove this method when resetPassword be deployed on all envs
  restore (data) {
    const {
      email
    } = data

    logger.debug('AuthService.restore', data)

    return restorePassword({ email })
  }

  /*
    @param {FormData} formData
  */
  initPasswordResetWithForm (formData) {
    const {
      id,
      ...values
    } = formData

    return initPasswordResetWithForm({ id, values })
  }

  passwordReset (formData) {
    logger.debug('AuthService.resetPassword', formData)

    return passwordReset(formData)
  }

  passwordResetWithForm (formData) {
    const {
      id,
      resetToken,
      ...values
    } = formData

    return passwordResetWithForm({ id, resetToken, values })
  }
}

const $account = new AccountManager()

export default $account
